import * as React from 'react';
import { connect } from 'react-redux';
import { IconButton, IContextualMenuItem } from 'office-ui-fabric-react';
import { Calendar, CalendarStatus, canCalendarBeDeleted, getDeleteCalendarTitle, ICalendarAttrs } from '../../../store/calendar/common';
import { Field, getOptions } from '../../../entities/Metadata';
import { nameof } from '../../../store/services/metadataService';
import { notUndefined } from '../../utils/common';
import EntityHeader from '../../common/EntityHeader';
import Logo from '../../common/Logo';
import { EntityType } from '../../../entities/common';
import { CalendarStatusMap } from '../../../store/CalendarsListStore';
import StageView from '../../views/list/columns/StageView';
import RemoveDialog from '../../common/RemoveDialog';
import { ApplicationState } from '../../../store';

interface IActions {
    cloneCalendar: () => void;
    removeCalendar: () => void;
    updateCalendarStatus: (status: CalendarStatus) => void;
    makeCalendarDefault: () => void;
}

type OwnProps = {
    entity: Calendar;
    actions: IActions;
}

type StateProps = {
    fields: Field[];
}

type Props = OwnProps & StateProps;

const CalendarHeader = (props: Props) => {
    const { entity, actions, fields } = props;
    const [isRemoveDialogOpen, setIsRemoveDialogOpen] = React.useState(false);
    
    const statusField = React.useMemo(() => {
        const field = fields.find(_ => _.name === nameof<ICalendarAttrs>('Status'));
        if (!field) {
            return undefined;
        }

        const updatedField = removeDraftOptionIfEntitiesExist(field, entity);
        delete updatedField.settings.options[entity.attributes.Status];
        
        return updatedField;
    }, [fields, entity]);

    const statuses = React.useMemo(() => statusField && entity.isEditable && !entity.isDefault && !entity.isUserCalendar
        ? getOptions(statusField).map(_ => {
            const status = Number(_.key);
            return {
                key: status.toString(),
                data: status,
                name: _.text,
                onClick: () => actions.updateCalendarStatus(status)
            };
        }) : [], 
    [statusField, actions.updateCalendarStatus]);

    const moreMenuItems: IContextualMenuItem[] = [
        buildSetCalendarAsDefaultAction(entity, actions.makeCalendarDefault),
        !entity.isUserCalendar && entity.isEditable ? {
            key: 'clone-calendar',
            iconProps: { iconName: 'Copy' },
            name: 'Clone Calendar',
            disabled: !entity.isEditable,
            onClick: () => actions.cloneCalendar()
        } : undefined,
        {
            key: 'delete-calendar',
            iconProps: { iconName: 'Delete' },
            name: 'Delete Calendar',
            className: 'more-deleteButton',
            disabled: !canCalendarBeDeleted(entity),
            title: getDeleteCalendarTitle(entity),
            onClick: () => setIsRemoveDialogOpen(true)
        }
    ].filter(notUndefined);
    
    return <>
        <EntityHeader
            entity={entity.isUserCalendar ? undefined : entity}
            name={entity.attributes.Name}
            nameTitle={entity.attributes.Name}
            logo={<Logo className="calendar-logo" />}
            leftSide={(
                <>
                    {entity.isDefault && <span className="entity-indicator default-process-indicator">(Default)</span>}
                    <StageView
                        entityType={EntityType.Calendar}
                        value={entity.attributes.Status}
                        className="calendar-status"
                        map={CalendarStatusMap}
                        subMenuItems={statuses}
                    />
                </>
            )}
            buttons={entity.isUserCalendar ? undefined : <IconButton
                menuIconProps={{ iconName: 'More' }}
                className='menu'
                text='More'
                disabled={!entity.isEditable}
                menuProps={{ items: moreMenuItems, className: 'section-menu' }}
            />}
        />
        {
            isRemoveDialogOpen && (
                <RemoveDialog
                    onClose={() => setIsRemoveDialogOpen(false)}
                    onComplete={() => { actions.removeCalendar(); }}
                    dialogContentProps={{
                        title: "Delete calendar",
                        subText: `Are you sure you want to delete calendar "${entity.attributes.Name}" ?`
                    }}
                    confirmButtonProps={{ text: "Delete" }}
                />
            )
        }
    </>;
}

export const buildSetCalendarAsDefaultAction = (calendar: Calendar, makeCalendarDefault: (id: string) => void) =>
    !calendar.isDefault && calendar.isEditable && !calendar.isUserCalendar
        ? {
              key: "set-as-default",
              name: "Set as Default",
              disabled: calendar.attributes.Status !== CalendarStatus.Active,
              title: calendar.attributes.Status === CalendarStatus.Active
                ? "Calendar marked as Default will be pre-selected in the Project Calendar field during Project creation"
                : "Calendar is not active",
              iconProps: { iconName: "ActivateOrders" },
              onClick: () => makeCalendarDefault(calendar.id),
          }
        : undefined;

export function removeDraftOptionIfEntitiesExist(statusField: Field, calendar: Calendar) {
    const fieldCopy = { ...statusField, settings: { ...statusField.settings, options: { ...statusField.settings?.options } } };
    if (calendar.projectIds?.length || calendar.resourceIds?.length) {
        delete fieldCopy.settings.options[CalendarStatus.Draft];
    }
    return fieldCopy;
}

function mapStateToProps(state: ApplicationState): StateProps {
    const fields = state.fields[EntityType.Calendar];
    return {
        fields: fields.allIds.map(_ => fields.byId[_]),
    };
}

export default connect(mapStateToProps)(CalendarHeader);
